import React, { useEffect, useState, useMemo } from 'react';
import { db } from '../firebase';
import { collection, getDocs } from 'firebase/firestore';
import { logError } from '../utils/log';
import { PointSeriesWeekSummary } from '../components/PointSeriesWeekSummary';

const gameStructure = [
    { name: '301 Singles', type: 'S301' }, { name: '301 Singles', type: 'S301' }, { name: '301 Doubles', type: 'D301' },
    { name: '501 Singles', type: 'S501' }, { name: '501 Singles', type: 'S501' }, { name: '501 Doubles', type: 'D501' },
    { name: 'Cricket Singles', type: 'SCricket' }, { name: 'Cricket Singles', type: 'SCricket' }, { name: 'Cricket Doubles', type: 'DCricket' },
];

// Helpers to determine calendar-week alignment and nearest scheduled week
const startOfWeek = (date) => {
    const d = new Date(date);
    d.setHours(0, 0, 0, 0); // Use local time, not UTC
    const day = d.getDay();
    const diff = d.getDate() - day;
    return new Date(d.setDate(diff));
};

const isSameCalendarWeek = (a, b) => startOfWeek(a).getTime() === startOfWeek(b).getTime();

const findWeekIndexForDate = (weeks, date) => {
    for (let i = 0; i < (weeks || []).length; i++) {
        if (!weeks[i].date) continue;
        
        // Robustly parse 'MM/DD/YYYY' to avoid timezone-related parsing issues.
        // new Date(string) can be inconsistent; new Date(y, m, d) is reliable.
        const dateStr = weeks[i].date;
        const parts = dateStr.split('/');
        if (parts.length !== 3) continue; // Skip invalid formats
        const wDate = new Date(parts[2], parts[0] - 1, parts[1]);

        if (!isNaN(wDate.getTime()) && isSameCalendarWeek(wDate, date)) {
            return i;
        }
    }
    return -1;
};

const getTuesdayOfWeek = (date) => {
    const start = startOfWeek(date); // Sunday
    const tuesday = new Date(start);
    tuesday.setDate(start.getDate() + 2); // Use local date method
    return tuesday;
};

export const SchedulePage = ({ loggedInUser, navigate, pageProps }) => {
    const [players, setPlayers] = useState([]);
    const [activeSchedule, setActiveSchedule] = useState(null);
    const [allSchedules, setAllSchedules] = useState([]);
    const [allTeams, setAllTeams] = useState([]);
    const [loading, setLoading] = useState(true);
    const [currentWeekIndex, setCurrentWeekIndex] = useState(-1);
    const [weeklyHighlights, setWeeklyHighlights] = useState({});
    const [displayPlaceholder, setDisplayPlaceholder] = useState(false);
    const [previewModalOpen, setPreviewModalOpen] = useState(false);
    const [previewData, setPreviewData] = useState(null);
    const [allPlayersStats, setAllPlayersStats] = useState({});
    const [calendarCursorDate, setCalendarCursorDate] = useState(() => {
        const d = new Date();
        d.setHours(0, 0, 0, 0); // Use local time
        return d;
    });

    const mysteryOutSummary = useMemo(() => {
        if (!activeSchedule || activeSchedule.type !== 'pointSeries' || currentWeekIndex === -1 || !activeSchedule.weeks[currentWeekIndex]) {
            return { pot: 0, winner: null };
        }

        const currentWeekNumber = activeSchedule.weeks[currentWeekIndex].week;
        const relevantMysteryOuts = (activeSchedule.mysteryOut || [])
            .filter(mo => mo.week <= currentWeekNumber)
            .sort((a, b) => a.week - b.week);

        let currentPot = 0;
        let winnerThisWeek = null;

        for (const entry of relevantMysteryOuts) {
            if (entry.player.toLowerCase() === 'x') {
                currentPot += entry.total || 0;
            } else {
                // A win occurred. The pot value for that week *was* the total payout.
                currentPot = entry.total || 0;
                if (entry.week === currentWeekNumber) {
                    winnerThisWeek = { player: entry.player, amount: currentPot };
                }
                // For subsequent weeks, the pot is reset.
                if (entry.week < currentWeekNumber) {
                    currentPot = 0;
                }
            }
        }
        return { pot: currentPot, winner: winnerThisWeek };
    }, [activeSchedule, currentWeekIndex]);

    const currentWeek = activeSchedule && currentWeekIndex !== -1 ? activeSchedule.weeks[currentWeekIndex] : null;

    const isNextWeekOrSooner = useMemo(() => {
        if (!currentWeek || !currentWeek.date) return true; // Default to showing if no date
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const eventDate = new Date(currentWeek.date);
        // Calculate the difference in days, rounding up to handle time-of-day differences
        const diffDays = Math.ceil((eventDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
        // Show if it's in the past, today, or within the next 7 days
        return diffDays <= 7;
    }, [currentWeek]);

    useEffect(() => {
        // This effect has been updated to accept pageProps for direct navigation
        const fetchScheduleData = async () => {
            setLoading(true);
            try {
                // Only fetch player names when user is authenticated
                if (loggedInUser) {
                    const playersSnapshot = await getDocs(collection(db, "users"));
                    setPlayers(playersSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
                } else {
                    setPlayers([]);
                }

                const teamsSnapshot = await getDocs(collection(db, "teams"));
                const teamsList = teamsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                setAllTeams(teamsList);

                // Fetch both schedules and point series
                const schedulesSnapshot = await getDocs(collection(db, "schedules"));
                const regularSchedules = schedulesSnapshot.docs.map(doc => ({ ...doc.data(), id: doc.id, type: 'schedule' }));

                const pointSeriesSnapshot = await getDocs(collection(db, "pointSeries"));
                const pointSeries = pointSeriesSnapshot.docs.map(doc => {
                    const seriesData = doc.data();
                    const weekInfoEntries = (seriesData.weekInfo || []).sort((a, b) => (a.week || 0) - (b.week || 0));

                    // Find the first week that has a valid date to use as an anchor.
                    const anchorWeekInfo = weekInfoEntries.find(wi => wi && wi.date && wi.week);
                    let anchorDate = null;
                    let anchorWeekNum = -1;

                    if (anchorWeekInfo) {
                        const parts = anchorWeekInfo.date.split('/');
                        if (parts.length === 3) {
                            // Create date object from MM/DD/YYYY
                            anchorDate = new Date(parts[2], parseInt(parts[0], 10) - 1, parts[1]);
                            anchorWeekNum = anchorWeekInfo.week;
                        }
                    }

                    const weeks = weekInfoEntries.map(wi => {
                        let finalDate = wi.date;
                        // If the date is missing AND we have a valid anchor AND the current week is valid
                        if (!finalDate && anchorDate && anchorWeekNum !== -1 && wi && wi.week) {
                            const dateForThisWeek = new Date(anchorDate.getTime());
                            const weekDifference = wi.week - anchorWeekNum;
                            dateForThisWeek.setDate(dateForThisWeek.getDate() + weekDifference * 7);
                            finalDate = dateForThisWeek.toLocaleDateString('en-US');
                        }
                        return { week: wi.week, date: finalDate, games: [] };
                    });

                    return { ...seriesData, id: doc.id, type: 'pointSeries', weeks };
                });

                const combinedSchedules = [...regularSchedules, ...pointSeries];

                // Sort all schedules/series. Prioritize non-archived, then by date/year.
                combinedSchedules.sort((a, b) => {
                    if (a.archived !== b.archived) return a.archived ? 1 : -1;
                    // For schedules in the same year, prioritize point series
                    const aYear = a.year || (a.createdAt ? new Date(a.createdAt).getFullYear().toString() : '0');
                    const bYear = b.year || (b.createdAt ? new Date(b.createdAt).getFullYear().toString() : '0');
                    if (aYear === bYear) {
                        if (a.type === 'pointSeries' && b.type !== 'pointSeries') return -1;
                        if (a.type !== 'pointSeries' && b.type === 'pointSeries') return 1;
                    }
                    // Fallback to year/date sort
                    return bYear.localeCompare(aYear) || (b.name || '').localeCompare(a.name || '');
                });

                setAllSchedules(combinedSchedules);
                
                // If pageProps are provided (e.g., from a 'Back' button), use them to set the initial state
                if (pageProps?.scheduleId && typeof pageProps?.weekIndex === 'number') {
                    const startingSchedule = combinedSchedules.find(s => s.id === pageProps.scheduleId);
                    if (startingSchedule) {
                        setActiveSchedule(startingSchedule);
                        setCurrentWeekIndex(pageProps.weekIndex);
                        setDisplayPlaceholder(false);
                        const weekDate = startingSchedule.weeks[pageProps.weekIndex]?.date;
                        if (typeof weekDate === 'string') {
                            const parts = weekDate.split('/');
                            if (parts.length === 3) {
                                const parsedDate = new Date(parts[2], parseInt(parts[0], 10) - 1, parts[1]);
                                setCalendarCursorDate(parsedDate);
                            }
                        }
                        setLoading(false);
                        return; // Exit early, we have our state
                    }
                }

                let startingSchedule = null; // The schedule to show on load
                let startingWeekIndex = -1;
                const today = new Date(); today.setHours(0, 0, 0, 0); // Use local time

                const activeSchedules = combinedSchedules.filter(s => !s.archived);

                // 1. Find a schedule with a week containing today's date
                for (const sched of activeSchedules) {
                    const idx = findWeekIndexForDate(sched.weeks || [], today);
                    if (idx !== -1) {
                        startingSchedule = sched;
                        startingWeekIndex = idx;
                        break;
                    }
                }

                // 2. If no current week, find the nearest future week
                if (!startingSchedule) {
                    let nearestFutureDate = Infinity;
                    let nearestFuture = null;

                    activeSchedules.forEach(sched => {
                        (sched.weeks || []).forEach((week, index) => {
                            if (!week.date) return;
                            const weekDate = new Date(week.date);
                            if (weekDate >= today) {
                                const diff = weekDate - today;
                                if (diff < nearestFutureDate) {
                                    nearestFutureDate = diff;
                                    nearestFuture = { schedule: sched, weekIndex: index };
                                }
                            }
                        });
                    });

                    if (nearestFuture) {
                        startingSchedule = nearestFuture.schedule;
                        startingWeekIndex = nearestFuture.weekIndex;
                    }
                }

                // 3. If still no schedule, fall back to the most recent past week of the latest active schedule
                if (!startingSchedule && activeSchedules.length > 0) {
                    startingSchedule = activeSchedules[0]; // Latest non-archived schedule
                    const weeks = startingSchedule.weeks || [];
                    let mostRecentPastIndex = -1;
                    let smallestDiff = -Infinity;

                    weeks.forEach((week, index) => {
                        if (!week.date) return;
                        const weekDate = new Date(week.date);
                        if (weekDate <= today) {
                            const diff = weekDate - today;
                            if (diff > smallestDiff) {
                                smallestDiff = diff;
                                mostRecentPastIndex = index;
                            }
                        }
                    });
                    startingWeekIndex = mostRecentPastIndex !== -1 ? mostRecentPastIndex : (weeks.length > 0 ? 0 : -1);
                }

                // 4. Final fallback for when there are no active schedules at all
                if (!startingSchedule && combinedSchedules.length > 0) {
                    startingSchedule = combinedSchedules[0]; // The absolute most recent schedule, even if archived
                    const weeks = startingSchedule.weeks || [];
                    let mostRecentPastIndex = -1;
                    let smallestDiff = -Infinity;
                    weeks.forEach((week, index) => {
                        if (!week.date) return;
                        const weekDate = new Date(week.date);
                        if (weekDate <= today) {
                            const diff = weekDate - today;
                            if (diff > smallestDiff) {
                                smallestDiff = diff;
                                mostRecentPastIndex = index;
                            }
                        }
                    });
                    startingWeekIndex = mostRecentPastIndex !== -1 ? mostRecentPastIndex : (weeks.length > 0 ? 0 : -1);
                }

                if (combinedSchedules.length > 0) {
                    setActiveSchedule(startingSchedule);
                    setCurrentWeekIndex(startingWeekIndex);
                    setDisplayPlaceholder(startingWeekIndex === -1);
                    // If we found a valid starting week, update the calendar cursor to match its date.
                    // Otherwise, it stays on today's date.
                    if (startingSchedule && startingWeekIndex !== -1) {
                        const weekDate = startingSchedule.weeks[startingWeekIndex]?.date;
                        if (typeof weekDate === 'string') {
                            // Set the cursor to the date of the found week to ensure navigation works from that point.
                            // Use robust parsing to match the logic in findWeekIndexForDate.
                            const parts = weekDate.split('/');
                            if (parts.length === 3) {
                                const parsedDate = new Date(parts[2], parts[0] - 1, parts[1]);
                                setCalendarCursorDate(parsedDate);
                            }
                        }
                    }
                }

                if (loggedInUser && startingSchedule && startingSchedule.type === 'schedule') {
                    const playersSnapshot = await getDocs(collection(db, "users"));
                    const playersList = playersSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                    setPlayers(playersList);

                    const pStats = {};
                    playersList.forEach(player => {
                        pStats[player.id] = {
                            w: 0, l: 0,
                            S301: { w: 0, l: 0 }, D301: { w: 0, l: 0 },
                            S501: { w: 0, l: 0 }, D501: { w: 0, l: 0 },
                            SCricket: { w: 0, l: 0 }, DCricket: { w: 0, l: 0 }
                        };
                    });

                    startingSchedule.weeks.forEach(week => {
                        if(!week.games) return;
                        week.games.forEach(match => {
                            if (match.results) {
                                if (Array.isArray(match.results.gameScores)) {
                                    match.results.gameScores.forEach((score, idx) => {
                                        const gameType = gameStructure[idx].type;
                                        const isSingles = gameType.startsWith('S');
                                        const pa = match.results.playerAssignments || {};
                                        const homePlayers = isSingles ? [pa[`${idx}-home-p1`]] : [pa[`${idx}-home-p1`], pa[`${idx}-home-p2`]];
                                        const awayPlayers = isSingles ? [pa[`${idx}-away-p1`]] : [pa[`${idx}-away-p1`], pa[`${idx}-away-p2`]];
                                        const homeWon = score.home > score.away;

                                        homePlayers.filter(Boolean).forEach(pid => { if(pStats[pid]) { if(homeWon) pStats[pid][gameType].w++; else pStats[pid][gameType].l++; } });
                                        awayPlayers.filter(Boolean).forEach(pid => { if(pStats[pid]) { if(!homeWon) pStats[pid][gameType].w++; else pStats[pid][gameType].l++; } });
                                    });
                                }
                            }
                        });
                    });
                    setAllPlayersStats(pStats);
                } else if (!loggedInUser) {
                    setPlayers([]);
                    setAllPlayersStats({});
                }
            } catch (error) {
                logError("Error fetching schedule data: ", error);
            }
            setLoading(false);
        };

        fetchScheduleData();
    }, [loggedInUser, pageProps]);

    // Effect to calculate weekly highlights when the current week changes
    useEffect(() => {
        if (!activeSchedule || currentWeekIndex === -1 || !activeSchedule.weeks[currentWeekIndex] || displayPlaceholder) {
            setWeeklyHighlights({});
            return;
        }

        const currentWeek = activeSchedule.weeks[currentWeekIndex];
        const aggregatedHighlights = {};

        currentWeek.games.forEach(game => {
            if (game.results?.highlights) {
                Object.entries(game.results.highlights).forEach(([playerId, playerHighlights]) => {
                    if (!aggregatedHighlights[playerId]) {
                        aggregatedHighlights[playerId] = {
                            totalHighlights: 0,
                            tons: [], total180s: 0,
                            highIns: [], highOuts: [],
                            totalC6: 0, totalC7: 0, totalC8: 0, totalC9: 0,
                        };
                    }
                    const stats = aggregatedHighlights[playerId];

                    if (playerHighlights.tons) {
                        const tons = playerHighlights.tons.split(',').map(t => parseInt(t.trim(), 10)).filter(t => !isNaN(t));
                        stats.tons.push(...tons);
                        stats.totalHighlights += tons.length;
                    }
                    if (playerHighlights.on80) {
                        const highIns = playerHighlights.on80.split(',').map(t => parseInt(t.trim(), 10)).filter(t => !isNaN(t));
                        stats.highIns.push(...highIns);
                        stats.totalHighlights += highIns.length;
                    }
                    if (playerHighlights.highOut) {
                        const highOuts = playerHighlights.highOut.split(',').map(t => parseInt(t.trim(), 10)).filter(t => !isNaN(t));
                        stats.highOuts.push(...highOuts);
                        stats.totalHighlights += highOuts.length;
                    }

                    const numericHighlights = ['180s', 'c6', 'c7', 'c8', 'c9'];
                    numericHighlights.forEach(key => {
                        if (playerHighlights[key]) {
                            const count = parseInt(playerHighlights[key], 10);
                            stats[`total${key.charAt(0).toUpperCase() + key.slice(1)}`] += count;
                            stats.totalHighlights += count;
                        }
                    });
                });
            }
        });

        setWeeklyHighlights(aggregatedHighlights);
    }, [currentWeekIndex, activeSchedule, displayPlaceholder]);

    // Check if the logged-in user's team is participating in a match
    const isMyMatch = (match) => {
        if (!loggedInUser || !loggedInUser.currentTeam) {
            return false;
        }
        const userTeamId = loggedInUser.currentTeam.id;
        return match.homeTeamId === userTeamId || match.awayTeamId === userTeamId;
    };

    const getTeamNameById = (teamId) => allTeams.find(t => t.id === teamId)?.name || 'Unknown Team';
    const getPlayerName = (playerId) => {
        const player = players.find(p => p.id === playerId);
        if (!player) return 'Unknown Player';
        return `${player.firstName} ${player.lastName}`;
    };

    const pickScheduleForDate = (date) => {
        if (!allSchedules || allSchedules.length === 0) return { schedule: null, weekIndex: -1 };
        // Try to find a schedule containing the calendar week
        for (const sched of allSchedules) {
            const idx = findWeekIndexForDate(sched.weeks || [], date);
            if (idx !== -1) return { schedule: sched, weekIndex: idx };
        }
        return { schedule: activeSchedule, weekIndex: -1 };
    };

    const handleNextWeek = () => {
        const next = new Date(calendarCursorDate);
        next.setDate(next.getDate() + 7);
        setCalendarCursorDate(next);
        const { schedule, weekIndex } = pickScheduleForDate(next);
        if (schedule) setActiveSchedule(schedule);
        setCurrentWeekIndex(weekIndex);
        setDisplayPlaceholder(weekIndex === -1);
    };
    const handlePrevWeek = () => {
        const prev = new Date(calendarCursorDate);
        prev.setDate(prev.getDate() - 7);
        setCalendarCursorDate(prev);
        const { schedule, weekIndex } = pickScheduleForDate(prev);
        if (schedule) setActiveSchedule(schedule);
        setCurrentWeekIndex(weekIndex);
        setDisplayPlaceholder(weekIndex === -1);
    };

    const handleOpenPreview = (game) => {
        if (!activeSchedule) return;

        const getMostLikelyPlayers = (teamId, schedule) => {
            const slots = Array(9).fill(null).map(() => ({}));
            schedule.weeks.forEach(week => {
                if (!week.games) return;
                week.games.forEach(g => {
                    if (!g.results) return;
                    const isHomeGame = g.homeTeamId === teamId;
                    const isAwayGame = g.awayTeamId === teamId;
                    if (!isHomeGame && !isAwayGame) return;

                    const prefix = isHomeGame ? 'home' : 'away';
                    for (let i = 0; i < 9; i++) {
                        const p1 = g.results.playerAssignments?.[`${i}-${prefix}-p1`];
                        const p2 = g.results.playerAssignments?.[`${i}-${prefix}-p2`];
                        if (p1) slots[i][p1] = (slots[i][p1] || 0) + 1;
                        if (p2) slots[i][p2] = (slots[i][p2] || 0) + 1;
                    }
                });
            });

            return slots.map((freqMap, idx) => {
                const sorted = Object.entries(freqMap).sort((a, b) => b[1] - a[1]);
                const isSingles = gameStructure[idx].type.startsWith('S');
                return isSingles ? (sorted.length > 0 ? [sorted[0][0]] : []) : (sorted.length > 1 ? [sorted[0][0], sorted[1][0]] : (sorted.length > 0 ? [sorted[0][0]] : []));
            });
        };

        const homeLikely = getMostLikelyPlayers(game.homeTeamId, activeSchedule);
        const awayLikely = getMostLikelyPlayers(game.awayTeamId, activeSchedule);
        setPreviewData({ 
            homeTeamName: getTeamNameById(game.homeTeamId), 
            awayTeamName: getTeamNameById(game.awayTeamId), 
            homeLikely, 
            awayLikely 
        });
        setPreviewModalOpen(true);
    };

    const hasGamesThisWeek = !!(currentWeek && Array.isArray(currentWeek.games) && currentWeek.games.length > 0);
    const showNoGamesCard = !displayPlaceholder && !!currentWeek && !hasGamesThisWeek && activeSchedule?.type !== 'pointSeries';

    const highlightEntries = Object.entries(weeklyHighlights)
        .filter(([, h]) => (h?.totalHighlights || 0) > 0)
        .sort((a, b) => (b[1]?.totalHighlights || 0) - (a[1]?.totalHighlights || 0));
    const hasHighlights = highlightEntries.length > 0;
    const topHighlightCount = highlightEntries[0]?.[1]?.totalHighlights || 0;

    return (
        <div className="space-y-2 md:space-y-6 w-full max-w-full overflow-x-hidden">
            <h2 className="page-title heading-primary-1">League Schedule</h2>

            <div className="surface-low rounded-md border-low p-2 sm:p-3 md:p-4 card-mobile-tight w-full max-w-full overflow-x-hidden">
                <h3 className="text-2xl font-bold heading-primary-1 text-center mb-2">
                    {loading
                        ? ' '
                        : displayPlaceholder
                        ? 'Schedule'
                        : activeSchedule?.type === 'pointSeries'
                        ? `${activeSchedule.name} - ${activeSchedule.year}`
                        : activeSchedule?.name || 'Schedule'
                    }
                    {activeSchedule?.archived && !loading && <span className="tag tag-neutral ml-2">Archived</span>}
                </h3>

                {/* Carousel Navigation (always render to keep layout width stable) */}
                <div className="mb-3 md:mb-4">
                    <div className="text-center mb-1 md:mb-2">
                        {loading ? (
                            <h4 className="text-xl font-semibold text-strong">Loading schedule…</h4>
                        ) : (
                            <h4 className="text-xl font-semibold text-strong">
                                {displayPlaceholder
                                    ? 'No Matches This Week'
                                    : currentWeek?.week === 'Event'
                                    ? currentWeek.games[0].eventName
                                    : `Week ${currentWeek?.week}` || 'No schedules yet'
                                }
                            </h4>
                        )}
                        <p className="text-muted">
                            {loading
                                ? ' '
                                : displayPlaceholder
                                ? getTuesdayOfWeek(calendarCursorDate).toLocaleDateString('en-US')
                                : currentWeek?.date || ' ' // Otherwise, show the date from the schedule data.
                            }
                            {activeSchedule?.archived && !loading && (
                                <span className="block mt-1 text-xs text-muted">Viewing archived season</span>
                            )}
                        </p>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 md:gap-3 -mx-3 sm:-mx-4 md:-mx-4">
                        <button onClick={handlePrevWeek} className="btn btn-primary w-full" disabled={loading || !activeSchedule}>
                            &larr; Previous Week
                        </button>
                        <button onClick={handleNextWeek} className="btn btn-primary w-full" disabled={loading || !activeSchedule}>
                            Next Week &rarr;
                        </button>
                    </div>
                </div>

                {/* Matches - Mobile friendly cards */}
                {loading ? (
                    <div className="card rounded-lg p-4 md:p-6 text-center">
                        <p className="text-muted">Loading…</p>
                    </div>                    
                ) : activeSchedule && currentWeek ? (
                    (displayPlaceholder || showNoGamesCard) ? (
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 sm:gap-3 lg:gap-4 grid-tight -mx-3 sm:-mx-4 md:-mx-4">
                            <div className="card rounded-lg p-4 md:p-6 text-center col-span-full">
                                <p className="text-strong">No league matches are scheduled for the current calendar week.</p>
                                <p className="text-sm text-muted mt-1">Use the arrows above to browse the season.</p>
                            </div>
                        </div>
                    ) : activeSchedule.type === 'pointSeries' ? (
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 -mx-3 sm:-mx-4 md:-mx-4">
                            <PointSeriesWeekSummary week={currentWeek} schedule={activeSchedule} navigate={navigate} />
                            <div className="card rounded-lg p-4 md:p-6">
                                <h4 className="text-xl font-bold heading-secondary-1 mb-3 text-center">Mystery Out</h4>
                                {isNextWeekOrSooner ? (
                                    mysteryOutSummary.winner ? (
                                        <div className="text-center">
                                            <p className="text-success font-bold text-lg">WINNER!</p>
                                            <p className="text-2xl font-extrabold text-strong">{mysteryOutSummary.winner.player}</p>
                                            <p className="text-3xl font-bold subheading-primary-1">${mysteryOutSummary.winner.amount}</p>
                                        </div>
                                    ) : (
                                        <div className="text-center"><p className="text-muted">Current Pot</p><p className="text-4xl font-bold subheading-primary-1">${mysteryOutSummary.pot}</p></div>
                                    )
                                ) : (   
                                        <div className="text-center">
                                            <p className="text-muted">Current Pot</p>
                                            <p className="text-4xl font-bold text-muted" title="Pot will be revealed closer to the event date">?</p>
                                        </div>
                                    )}
                            </div>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 sm:gap-3 lg:gap-4 grid-tight -mx-3 sm:-mx-4 md:-mx-4">
                            {currentWeek.games.map((game, gameIndex) => (
                                game.eventName ? (
                                    <div key={gameIndex} className="surface-low rounded-md p-2 sm:p-3 text-center border-low">
                                        <p className="font-bold text-warning">{game.eventName}</p>
                                        {game.venue && (
                                            <p className="mt-1 text-sm text-muted">Venue: {game.venue}</p>
                                        )}
                                    </div>
                                ) : (
                                    <div
                                        key={gameIndex}
                                        className={`surface-low rounded-md p-2 sm:p-3 border-low ${loggedInUser && (game.result || (!game.result && game.homeTeamId !== 'TBD' && game.awayTeamId !== 'TBD')) ? 'cursor-pointer hover:opacity-80' : 'cursor-default'} ${isMyMatch(game) ? 'my-match-card' : ''}`}
                                        onClick={
                                            loggedInUser 
                                            ? (game.result ? () => navigate('Match Results Summary', { scheduleId: activeSchedule.id, weekIndex: currentWeekIndex, gameIndex: gameIndex, from: 'Schedule' }) : (game.homeTeamId !== 'TBD' && game.awayTeamId !== 'TBD' ? () => handleOpenPreview(game) : undefined)) 
                                            : undefined
                                        }
                                        role={loggedInUser && (game.result || (!game.result && game.homeTeamId !== 'TBD' && game.awayTeamId !== 'TBD')) ? 'button' : undefined}
                                    >
                                        <div className="flex items-center justify-between gap-2 min-w-0">
                                            <div className="flex-1 min-w-0 text-right font-semibold text-strong leading-tight whitespace-normal break-words">
                                                {game.awayTeamId === 'TBD'
                                                    ? (game.advancement?.away ? `Winner of ${game.advancement.away.from}` : 'TBD')
                                                    : getTeamNameById(game.awayTeamId)}
                                            </div>
                                            <div className="mx-2 sm:mx-3 text-muted">
                                                {game.result ? (
                                                    <span className="font-bold text-lg subheading-primary-1">{game.result.split('-')[1].trim()} - {game.result.split('-')[0].trim()}</span>
                                                ) : (
                                                    <span>at</span>
                                                )}
                                            </div>
                                            <div className="flex-1 min-w-0 text-left font-semibold text-strong leading-tight whitespace-normal break-words">
                                                {game.homeTeamId === 'TBD'
                                                    ? (game.advancement?.home ? `Winner of ${game.advancement.home.from}` : 'TBD')
                                                    : getTeamNameById(game.homeTeamId)}
                                            </div>
                                        </div>
                                        <div className="mt-1 sm:mt-2 text-sm text-muted text-center">
                                            Venue: {game.venue}
                                        </div>
                                        {loggedInUser && game.result && (
                                            <div className="mt-1 sm:mt-2 text-xs text-muted text-center">Tap to view summary</div>
                                        )}
                                        {loggedInUser && !game.result && game.homeTeamId !== 'TBD' && game.awayTeamId !== 'TBD' && (
                                            <div className="mt-1 sm:mt-2 text-xs text-info font-medium text-center">Tap to view Match Preview</div>
                                        )}
                                    </div>
                                )
                            ))}
                        </div>
                    )
                ) : (
                    <div className="card rounded-lg p-4 md:p-6 text-center">
                        <p className="text-strong">No league matches are scheduled for the current calendar week.</p>
                        <p className="text-sm text-muted mt-1">Use the arrows above to browse the season.</p>
                    </div>
                )}

                {/* BYE Week Display */}
                {!loading && !displayPlaceholder && currentWeek?.byes && currentWeek.byes.length > 0 && (
                    <div className="mt-3 md:mt-4 text-center surface-low p-2 rounded-md">
                        <p className="text-sm text-muted"><strong>BYE:</strong> {currentWeek.byes.join(', ')}</p>
                    </div>
                )}
            </div>

            {/* Weekly Highlights Section - visible to authenticated users only (only render when there are highlights) */}
            {loggedInUser && !displayPlaceholder && hasHighlights && (
                <div className="card rounded-lg shadow-md p-3 sm:p-4 md:p-6 mt-4 md:mt-6 -mx-3 sm:-mx-4 md:-mx-4">
                    <h3 className="text-2xl font-bold heading-secondary-1 text-center mb-3 md:mb-4">Highlights of the Week</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2 md:gap-4 grid-tight">
                        {highlightEntries.map(([playerId, highlights]) => {
                            const isLeader = (highlights?.totalHighlights || 0) === topHighlightCount;
                            return (
                            <div key={playerId} className={`card p-3 sm:p-4 rounded-lg ${isLeader ? 'highlight-leader-card' : ''}`}>
                                <p className="font-bold text-lg subheading-primary-1 flex items-center gap-2">
                                    {isLeader && (
                                        <span className="leader-star" title="Most highlights" aria-label="Most highlights">
                                            <svg viewBox="0 0 20 20" width="16" height="16" aria-hidden="true" focusable="false">
                                                <path d="M10 1.5l2.63 5.33 5.88.86-4.26 4.15 1 5.85L10 14.9l-5.25 2.75 1-5.85-4.26-4.15 5.88-.86L10 1.5z" fill="currentColor" />
                                            </svg>
                                        </span>
                                    )}
                                    <span>{getPlayerName(playerId)}</span>
                                    <span className="text-sm text-muted">({highlights.totalHighlights} Total)</span>
                                </p>
                                <ul className="text-sm mt-2 space-y-0.5 md:space-y-1">
                                    {highlights.total180s > 0 && (
                                        <li className="flex items-center gap-2">
                                            <span className="tag tag-danger">180s</span>
                                            <span>{highlights.total180s}</span>
                                        </li>
                                    )}
                                    {highlights.tons.length > 0 && (
                                        <li className="flex items-center gap-2">
                                            <span className="tag tag-primary-one">Tons</span>
                                            <span>({highlights.tons.length}): {highlights.tons.join(', ')}</span>
                                        </li>
                                    )}
                                    {highlights.highIns.length > 0 && (
                                        <li className="flex items-center gap-2">
                                            <span className="tag tag-info">High Ins</span>
                                            <span>({highlights.highIns.length}): {highlights.highIns.join(', ')}</span>
                                        </li>
                                    )}
                                    {highlights.highOuts.length > 0 && (
                                        <li className="flex items-center gap-2">
                                            <span className="tag tag-success">High Outs</span>
                                            <span>({highlights.highOuts.length}): {highlights.highOuts.join(', ')}</span>
                                        </li>
                                    )}
                                    {highlights.totalC9 > 0 && (
                                        <li className="flex items-center gap-2">
                                            <span className="tag tag-warning">9-Hits</span>
                                            <span>{highlights.totalC9}</span>
                                        </li>
                                    )}
                                    {highlights.totalC8 > 0 && (
                                        <li className="flex items-center gap-2">
                                            <span className="tag tag-warning">8-Hits</span>
                                            <span>{highlights.totalC8}</span>
                                        </li>
                                    )}
                                    {highlights.totalC7 > 0 && (
                                        <li className="flex items-center gap-2">
                                            <span className="tag tag-warning">7-Hits</span>
                                            <span>{highlights.totalC7}</span>
                                        </li>
                                    )}
                                    {highlights.totalC6 > 0 && (
                                        <li className="flex items-center gap-2">
                                            <span className="tag tag-warning">6-Hits</span>
                                            <span>{highlights.totalC6}</span>
                                        </li>
                                    )}
                                </ul>
                            </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {previewModalOpen && previewData && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-2 sm:p-4 bg-black bg-opacity-60" onClick={() => setPreviewModalOpen(false)}>
                    <div className="card rounded-lg shadow-xl w-full max-w-3xl max-h-[95vh] flex flex-col" onClick={e => e.stopPropagation()}>
                        <div className="flex justify-between items-center p-3 sm:p-4 border-b border-low">
                            <h3 className="text-xl font-bold heading-primary-1">Match Preview</h3>
                            <button onClick={() => setPreviewModalOpen(false)} className="text-muted hover:text-strong text-2xl leading-none">&times;</button>
                        </div>
                        <div className="p-4 overflow-y-auto space-y-4">
                            <p className="text-center text-muted text-sm mb-2">
                                Predicted matchups based on this season's data.
                            </p>
                            
                            <div className="hidden sm:block overflow-x-auto rounded-lg border border-low">
                                <table className="min-w-full text-sm">
                                    <thead className="table-header">
                                        <tr>
                                            <th className="py-2 px-3 text-left font-semibold">Game</th>
                                            <th className="py-2 px-3 text-left font-semibold">{previewData.awayTeamName}</th>
                                            <th className="py-2 px-3 text-center text-muted font-semibold">vs</th>
                                            <th className="py-2 px-3 text-right font-semibold">{previewData.homeTeamName}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {gameStructure.map((game, idx) => {
                                            const getPlayerDisplay = (id, gameType) => {
                                                const p = players.find(u => u.id === id);
                                                const name = p ? (p.nickname || p.firstName) : 'Unknown';
                                                const stats = allPlayersStats[id]?.[gameType];
                                                const record = stats ? `(${stats.w}-${stats.l})` : '(0-0)';
                                                return `${name} ${record}`;
                                            };
                                            const awayPlayers = previewData.awayLikely[idx].map(id => getPlayerDisplay(id, game.type)).join(' & ') || 'TBD';
                                            const homePlayers = previewData.homeLikely[idx].map(id => getPlayerDisplay(id, game.type)).join(' & ') || 'TBD';
                                            return (
                                                <tr key={idx} className="border-b border-low row-alt">
                                                    <td className="py-3 px-3 font-medium text-strong">{game.name}</td>
                                                    <td className="py-3 px-3">{awayPlayers}</td>
                                                    <td className="py-3 px-3 text-center text-muted text-xs font-bold">VS</td>
                                                    <td className="py-3 px-3 text-right">{homePlayers}</td>
                                                </tr>
                                            )
                                        })}
                                    </tbody>
                                </table>
                            </div>

                            <div className="sm:hidden space-y-2">
                                {gameStructure.map((game, idx) => {
                                    const getPlayerDisplay = (id, gameType) => {
                                        const p = players.find(u => u.id === id);
                                        const name = p ? (p.nickname || p.firstName) : 'Unknown';
                                        const stats = allPlayersStats[id]?.[gameType];
                                        const record = stats ? `(${stats.w}-${stats.l})` : '(0-0)';
                                        return `${name} ${record}`;
                                    };
                                    const awayPlayers = previewData.awayLikely[idx].length > 0 ? previewData.awayLikely[idx].map(id => <div key={id} className="truncate">{getPlayerDisplay(id, game.type)}</div>) : 'TBD';
                                    const homePlayers = previewData.homeLikely[idx].length > 0 ? previewData.homeLikely[idx].map(id => <div key={id} className="truncate">{getPlayerDisplay(id, game.type)}</div>) : 'TBD';
                                    return (
                                        <div key={idx} className="surface-low p-3 rounded-lg border border-low">
                                            <div className="text-xs font-bold text-muted uppercase tracking-wider mb-2 text-center">{game.name}</div>
                                            <div className="flex justify-between items-center gap-2">
                                                <div className="flex-1 min-w-0 text-sm font-medium text-left">{awayPlayers}</div>
                                                <div className="text-[10px] text-muted font-bold px-1 shrink-0">VS</div>
                                                <div className="flex-1 min-w-0 text-sm font-medium text-right">{homePlayers}</div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};